import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Plus, DollarSign, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AddBuyinDialog } from "./AddBuyinDialog";
import { ChipEntryDialog } from "./ChipEntryDialog";
import { GameSummary } from "./GameSummary";
import { PaymentTransfers } from "./PaymentTransfers";
import { Footer } from "./Footer";
import { Badge } from "@/components/ui/badge";
import { usePWAStandalone } from "@/hooks/usePWAStandalone";
import { getPlayerDisplayName, getPlayerDisplayNameById } from "@/utils/displayUtils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuthDebug } from "@/hooks/useAuthDebug";
import { useGroupPoints } from "@/hooks/useGroupPoints";

interface Game {
  id: string;
  title?: string;
  location?: string;
  description?: string;
  buyin_amount: number;
  chips_per_buyin: number;
  status: string;
  created_at: string;
  group_id: string;
}

interface Player {
  id: string;
  name: string;
  auth_user_id?: string;
  profiles?: {
    display_name?: string;
  } | null;
}

interface Buyin {
  id: string;
  player_id: string;
  amount: number;
  chips: number;
  created_at: string;
  players: { 
    name: string;
    auth_user_id?: string;
    profiles?: {
      display_name?: string;
    } | null;
  };
}

interface ChipEntry {
  id: string;
  player_id: string;
  final_chips: number;
  players: { 
    name: string;
    auth_user_id?: string;
    profiles?: {
      display_name?: string;
    } | null;
  };
}

export const GameView = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  
  const [game, setGame] = useState<Game | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [buyins, setBuyins] = useState<Buyin[]>([]);
  const [chipEntries, setChipEntries] = useState<ChipEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddBuyin, setShowAddBuyin] = useState(false);
  const [showChipEntry, setShowChipEntry] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const isStandalone = usePWAStandalone();

  const { user, session, isReady } = useAuthDebug();
  const { data: groupPoints } = useGroupPoints(game?.group_id, game?.status);
  const pointsByPlayer: Record<string, number> = {};
  if (groupPoints && gameId) {
    for (const [pid, p] of Object.entries(groupPoints.byPlayer)) {
      if (p.perGame[gameId] !== undefined) pointsByPlayer[pid] = p.perGame[gameId];
    }
  }

  useEffect(() => {
    if (!gameId) return;
    if (!isReady) return; // wait until auth state is ready
    if (!session) {
      const redirectPath = window.location.pathname + window.location.search;
      navigate(`/?login=1&redirect=${encodeURIComponent(redirectPath)}`);
      return;
    }
    fetchGameData();
  }, [gameId, isReady, session?.access_token]);

  const fetchGameData = async () => {
    try {
      setLoading(true);
      console.log('Fetching game data for game ID:', gameId);
      
      // Fetch game details
      const { data: gameData, error: gameError } = await supabase
        .from('games')
        .select('*')
        .eq('id', gameId)
        .single();

      if (gameError) throw gameError;
      setGame(gameData);

      // Fetch players from the group
      const { data: playersData, error: playersError } = await supabase
        .from('players')
        .select('*')
        .eq('group_id', gameData.group_id);
      
      // Enhance players with profile data
      const playersWithProfiles = await Promise.all((playersData || []).map(async (player) => {
        if (player.auth_user_id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('display_name')
            .eq('id', player.auth_user_id)
            .maybeSingle();
          return { ...player, profiles: profile };
        }
        return { ...player, profiles: null };
      }));

      if (playersError) throw playersError;
      setPlayers(playersWithProfiles);

      // Fetch buyins
      const { data: buyinsData, error: buyinsError } = await supabase
        .from('buyins')
        .select(`
          *,
          players(name, auth_user_id)
        `)
        .eq('game_id', gameId)
        .order('created_at', { ascending: false });

      // Enhance buyins with profile data
      const buyinsWithProfiles = await Promise.all((buyinsData || []).map(async (buyin) => {
        if (buyin.players.auth_user_id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('display_name')
            .eq('id', buyin.players.auth_user_id)
            .maybeSingle();
          return { 
            ...buyin, 
            players: { 
              ...buyin.players, 
              profiles: profile 
            } 
          };
        }
        return { 
          ...buyin, 
          players: { 
            ...buyin.players, 
            profiles: null 
          } 
        };
      }));

      if (buyinsError) throw buyinsError;
      setBuyins(buyinsWithProfiles);

      // Fetch chip entries
      const { data: chipEntriesData, error: chipEntriesError } = await supabase
        .from('chip_entries')
        .select(`
          *,
          players(name, auth_user_id)
        `)
        .eq('game_id', gameId);

      // Enhance chip entries with profile data
      const chipEntriesWithProfiles = await Promise.all((chipEntriesData || []).map(async (entry) => {
        if (entry.players.auth_user_id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('display_name')
            .eq('id', entry.players.auth_user_id)
            .maybeSingle();
          return { 
            ...entry, 
            players: { 
              ...entry.players, 
              profiles: profile 
            } 
          };
        }
        return { 
          ...entry, 
          players: { 
            ...entry.players, 
            profiles: null 
          } 
        };
      }));

      if (chipEntriesError) throw chipEntriesError;
      setChipEntries(chipEntriesWithProfiles);

    } catch (error) {
      console.error('Error fetching game data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch game data. Please sign in to continue.",
        variant: "destructive"
      });
      const redirectPath = window.location.pathname + window.location.search;
      navigate(`/?login=1&redirect=${encodeURIComponent(redirectPath)}`);

    } finally {
      setLoading(false);
    }
  };

  const settleGame = async () => {
    if (!game || chipEntries.length !== getPlayingPlayers().length) {
      toast({
        title: "Error",
        description: "All players must enter their final chip counts",
        variant: "destructive"
      });
      return;
    }

    try {
      // Calculate payouts
      const payouts = calculatePayouts();
      
      // Insert payouts
      const { error: payoutsError } = await supabase
        .from('payouts')
        .insert(payouts);

      if (payoutsError) throw payoutsError;

      // Update game status
      const { error: gameError } = await supabase
        .from('games')
        .update({ 
          status: 'settled',
          settled_at: new Date().toISOString()
        })
        .eq('id', gameId);

      if (gameError) throw gameError;

      toast({
        title: "Success",
        description: "Game settled successfully"
      });

      // Navigate to simplified payouts screen
      navigate(`/game/${gameId}/payouts`);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to settle game",
        variant: "destructive"
      });
    }
  };

  const getPlayingPlayers = () => {
    const playerIds = [...new Set(buyins.map(buyin => buyin.player_id))];
    return players.filter(player => playerIds.includes(player.id));
  };

  const calculatePayouts = () => {
    const playingPlayers = getPlayingPlayers();
    const chipRatio = game!.buyin_amount / game!.chips_per_buyin;
    
    return playingPlayers.map(player => {
      const playerBuyins = buyins.filter(b => b.player_id === player.id);
      const totalChipsBought = playerBuyins.reduce((sum, buyin) => sum + buyin.chips, 0);
      
      const chipEntry = chipEntries.find(ce => ce.player_id === player.id);
      const finalChips = chipEntry?.final_chips || 0;
      
      const netChips = finalChips - totalChipsBought;
      const netAmount = netChips * chipRatio;
      
      return {
        game_id: gameId!,
        player_id: player.id,
        net_chips: netChips,
        net_amount: netAmount
      };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading game data...</p>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Game not found</h2>
          <Button onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Groups
          </Button>
        </div>
      </div>
    );
  }

  const playingPlayers = getPlayingPlayers();
  const totalBuyins = buyins.reduce((sum, buyin) => sum + buyin.amount, 0);
  const totalChipsBought = buyins.reduce((sum, buyin) => sum + buyin.chips, 0);
  const totalChipsOnTable = chipEntries.reduce((sum, entry) => sum + entry.final_chips, 0);
  const chipsRemaining = totalChipsBought - totalChipsOnTable;
  const canSettle = game.status === 'in_progress' && chipEntries.length === playingPlayers.length && chipsRemaining === 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/group/${game.group_id}`)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">
                {game.title || "Untitled Game"}
              </h1>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <span>{playingPlayers.length} players</span>
                <span>•</span>
                <span>₹{game.buyin_amount} buy-in</span>
                <span>•</span>
                <Badge variant={game.status === 'in_progress' ? 'default' : 'secondary'}>
                  {game.status === 'in_progress' ? 'Active' : 'Settled'}
                </Badge>
              </div>
            </div>
          </div>
          {game.status === 'in_progress' && (
            <div className="hidden md:flex space-x-2">
              <Button variant="outline" onClick={() => setShowAddBuyin(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Buy-in
              </Button>
              <Button variant="outline" onClick={() => setShowChipEntry(true)}>
                Player Check-out
                {chipsRemaining > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {chipsRemaining.toLocaleString()}
                  </Badge>
                )}
              </Button>
              {canSettle && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button>
                      Settle Game
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Are you sure you want to settle game?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Once game is settled, changes will not be allowed to be made.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>No</AlertDialogCancel>
                      <AlertDialogAction onClick={settleGame}>Yes</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          )}
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Mobile Action Buttons */}
        {game.status === 'in_progress' && (
          <div className="md:hidden mb-6 space-y-3">
            <div className="flex space-x-2">
              <Button variant="outline" onClick={() => setShowAddBuyin(true)} className="flex-1">
                <Plus className="h-4 w-4 mr-2" />
                Add Buy-in
              </Button>
              <Button variant="outline" onClick={() => setShowChipEntry(true)} className="flex-1">
                Player Check-out
                {chipsRemaining > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {chipsRemaining.toLocaleString()}
                  </Badge>
                )}
              </Button>
            </div>
            {canSettle && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button className="w-full">
                    Settle Game
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure you want to settle game?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Once game is settled, changes will not be allowed to be made.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>No</AlertDialogCancel>
                    <AlertDialogAction onClick={settleGame}>Yes</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
        
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Buy-ins</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">₹{totalBuyins.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Players</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{playingPlayers.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Chips on Table</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {chipsRemaining.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">
                {chipEntries.length} / {playingPlayers.length} entries
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Game Content */}
        <Tabs defaultValue={searchParams.get('tab') || "summary"} className="space-y-6">
          <TabsList>
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="buyins">Buy-ins</TabsTrigger>
            <TabsTrigger value="chips">Final Chip Count</TabsTrigger>
            <TabsTrigger value="transfers">₹ Transfers</TabsTrigger>
          </TabsList>

          <TabsContent value="summary">
            <GameSummary
              game={game}
              players={playingPlayers}
              buyins={buyins}
              chipEntries={chipEntries}
              pointsByPlayer={pointsByPlayer}
            />
          </TabsContent>

          <TabsContent value="buyins">
            <Card>
              <CardHeader>
                <CardTitle>Buy-in History</CardTitle>
              </CardHeader>
              <CardContent>
                {buyins.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No buy-ins recorded yet</p>
                ) : (
                  <div className="space-y-2">
                    {buyins.map((buyin) => (
                      <div key={buyin.id} className="flex justify-between items-center p-3 bg-muted/50 rounded">
                        <div>
                          <span className="font-medium">{getPlayerDisplayName(buyin.players)}</span>
                          <span className="text-sm text-muted-foreground ml-2">
                            {new Date(buyin.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">₹{buyin.amount}</div>
                          <div className="text-sm text-muted-foreground">{buyin.chips} chips</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="chips">
            <Card>
              <CardHeader>
                <CardTitle>Final Chip Counts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {playingPlayers.map((player) => {
                    const chipEntry = chipEntries.find(ce => ce.player_id === player.id);
                    return (
                      <div key={player.id} className="flex justify-between items-center p-3 bg-muted/50 rounded">
                        <span className="font-medium">{getPlayerDisplayName(player)}</span>
                        {game.status === 'settled' ? (
                          <span className="font-medium">
                            {chipEntry ? `${chipEntry.final_chips.toLocaleString()} chips` : 'Not entered'}
                          </span>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedPlayerId(player.id);
                              setShowChipEntry(true);
                            }}
                          >
                            {chipEntry ? `Edit: ${chipEntry.final_chips.toLocaleString()} chips` : 'Enter chips'}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transfers">
            <PaymentTransfers gameId={gameId!} />
          </TabsContent>
        </Tabs>

        <AddBuyinDialog
          open={showAddBuyin}
          onOpenChange={setShowAddBuyin}
          gameId={gameId!}
          players={players}
          game={game}
          onBuyinAdded={fetchGameData}
        />

        <ChipEntryDialog
          open={showChipEntry}
          onOpenChange={(open) => {
            setShowChipEntry(open);
            if (!open) setSelectedPlayerId("");
          }}
          gameId={gameId!}
          players={playingPlayers}
          existingEntries={chipEntries}
          onChipEntryAdded={fetchGameData}
          gameStatus={game.status}
          buyins={buyins}
          selectedPlayerId={selectedPlayerId}
        />
      </div>
      {!isStandalone && <Footer />}
    </div>
  );
};